import React, { useState } from 'react';
import type { Nullable } from 'corgi-types';

import Alert from 'common/components/Alert';
import { Flex } from 'common/components/Flex';
import FormSubmitButton from 'common/components/Form/FormSubmitButton';
import { HeadlineH5 } from 'common/components/Typography';
import { MessageType } from 'common/enums/Alert';
import { useLocalization } from 'common/providers/LocaleProvider';
import { useSelectContactFormComponent } from '../hooks/contact-form';
import { ContactFormContent } from './Content';
import { Dropdown } from './Dropdown';
import { headlineStyles } from './Headline';
import type { RequestReasons, Settings, UserData } from './types';

interface ContactFormProps {
  requestReasons: RequestReasons;
  isLoggedIn: boolean;
  isRedirected: boolean;
  userData: Nullable<UserData>;
  reasonFromUrl: Nullable<string>;
  settings: Settings;
  skuFromUrl: Nullable<string>;
  sellerName: Nullable<string>;
  orderIdFromUrl: Nullable<string>;
}

const HEADING_ID = 'contact-form-heading';

const ContactForm = ({
  requestReasons: { reasons },
  isRedirected,
  reasonFromUrl,
  skuFromUrl,
  sellerName,
  orderIdFromUrl,
  ...restProps
}: ContactFormProps) => {
  const { t, r } = useLocalization();
  const [isChannelSelectionActive, setIsChannelSelectionActive] = useState(false);
  const {
    slices,
    setSlices,
    selectedReason,
    hasError,
    isDisabled,
    isLoading,
    fetchFormType,
    initialReason,
    setSelectedReason,
  } = useSelectContactFormComponent({
    isRedirected,
    reasonFromUrl,
    reasons,
  });
  const shouldRenderForm = selectedReason && slices.length > 0;
  const isSellerContactMode = !!sellerName;

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    fetchFormType();
  };

  const headline = isSellerContactMode
    ? r.t('h24_seller_contact_form_headline', { sellerName })
    : t('h24_contact_form_headline');

  return (
    <Flex data-corgi-id="contact-form" flexDirection="column">
      {hasError && (
        <Alert mb={['sp_16', 'sp_20']} type={MessageType.Error}>
          {t('h24_something_went_wrong')}
        </Alert>
      )}

      {!isChannelSelectionActive && !isSellerContactMode && (
        <form onSubmit={onFormSubmit}>
          <HeadlineH5 as="h1" id={HEADING_ID} {...headlineStyles}>
            {headline}
          </HeadlineH5>

          <Dropdown
            reasons={reasons}
            ariaLabelledBy={HEADING_ID}
            onSelect={setSelectedReason}
            initialReason={initialReason}
          />

          {!shouldRenderForm && (
            <FormSubmitButton
              defaultButtonLabel={t('h24_contact_form_select_topic')}
              isDisabled={isDisabled}
              isLoading={isLoading}
            />
          )}
        </form>
      )}

      {isSellerContactMode && (
        <HeadlineH5 as="h1" id={HEADING_ID} {...headlineStyles}>
          {headline}
        </HeadlineH5>
      )}

      {shouldRenderForm && (
        <ContactFormContent
          slices={slices}
          setSlices={setSlices}
          reason={selectedReason}
          skuFromUrl={skuFromUrl}
          orderIdFromUrl={orderIdFromUrl}
          sellerName={sellerName}
          isChannelSelectionActive={isChannelSelectionActive}
          setIsChannelSelectionActive={setIsChannelSelectionActive}
          {...restProps}
        />
      )}
    </Flex>
  );
};

export default ContactForm;
